/**
 * usePresenceMap
 * --------------
 * Admin-only React hook. Fetches the full `/api/admin/presence` roster
 * once + polls every 30s and returns a `Map<userId, { status, last_seen_at }>`
 * plus a `statusFor(id)` convenience getter.
 *
 * Why not a per-user fetch? Presence dots often appear in lists (workload
 * tabs, chat rosters, planner queues), so a single call keeps the network
 * cost O(1) regardless of how many rows are on screen.
 *
 * Usage:
 *   const { statusFor } = usePresenceMap();
 *   <PresenceDot status={statusFor(user.id)} />
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const REFRESH_MS = 30_000;

export function usePresenceMap({ enabled = true } = {}) {
    const [map, setMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const timerRef = useRef(null);

    const fetchOnce = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/presence");
            const next = new Map();
            for (const u of data?.items || []) {
                next.set(u.id, { status: u.status, last_seen_at: u.last_seen_at });
            }
            setMap(next);
        } catch {
            /* silent — presence is best-effort */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        fetchOnce();
        timerRef.current = setInterval(fetchOnce, REFRESH_MS);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [enabled, fetchOnce]);

    const statusFor = useCallback(
        (userId) => (userId ? map.get(userId)?.status || "offline" : "offline"),
        [map],
    );

    return { map, statusFor, loading, refresh: fetchOnce };
}
